import { useState, useCallback, useEffect, useRef } from "react";
import Sidebar from "./components/Sidebar";
import ChatPanel from "./chat/ChatPanel";
import StoryPanel from "./story/StoryPanel";
import HistoryPanel from "./history/HistoryPanel";
import LearningBoard from "./learning/LearningBoard";
import { ManageLessonsPanel } from "./manage-lessons";
import { AiTutorPanel } from "./ai-tutor";
import { MonitorPanel } from "./monitor";
import { AiLogsPanel } from "./ai-logs";
import { ProfilePanel } from "./profile";
import { SupportPanel } from "./support";
import { SentenceBoard } from "./sentences";
import { apiFetch } from "../../api";
import { trackEvent } from "../../analytics/posthog";
import s from "./AdminChatPage.module.css";

export default function AdminChatPage({ onLogout, userEmail, userRole }) {
  const [activeTab, setActiveTab] = useState("History");
  const [userPrompt, setUserPrompt] = useState("");
  const [activeStory, setActiveStory] = useState(null);
  const [story, setStory] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [error, setError] = useState("");
  const [chatOpen, setChatOpen] = useState(true);
  const [chatMode, setChatMode] = useState("chat"); // "chat" | "supertext"
  const [superTextPrompt, setSuperTextPrompt] = useState("");
  const [learningStoryId, setLearningStoryId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [downloadAudioStatus, setDownloadAudioStatus] = useState(null);
  const [downloadAudioGenerating, setDownloadAudioGenerating] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState("");
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  // Fetch download audio status when story changes or audio flags change
  useEffect(() => {
    if (!activeStory || !story?.are_audio_lesson_generated) {
      setDownloadAudioStatus(null);
      return;
    }

    let cancelled = false;
    async function fetchStatus() {
      try {
        const res = await apiFetch(`/api/v1/admin/download-audio-status/${activeStory}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setDownloadAudioStatus(data.status);
        }
      } catch {
        // ignore
      }
    }
    fetchStatus();
    return () => { cancelled = true; };
  }, [activeStory, story?.are_audio_lesson_generated, story?.are_audio_quizzes_generated, story?.are_words_info_generated]);

  // Fetch support unread count on mount and when leaving Support tab
  const fetchSupportUnreadCount = useCallback(async () => {
    try {
      const res = await apiFetch("/api/v1/support/unread-count");
      if (res.ok) {
        const data = await res.json();
        setSupportUnreadCount(data.count);
      }
    } catch {
      // badge is non-critical
    }
  }, []);

  useEffect(() => {
    fetchSupportUnreadCount();
  }, [fetchSupportUnreadCount]);

  useEffect(() => {
    if (activeTab !== "Support") {
      fetchSupportUnreadCount();
    }
  }, [activeTab, fetchSupportUnreadCount]);

  const isFirstTab = useRef(true);
  useEffect(() => {
    if (isFirstTab.current) { isFirstTab.current = false; return; }
    trackEvent("tab_viewed", { tab: activeTab });
  }, [activeTab]);

  // Fetch lessons once on mount, cache in state
  useEffect(() => {
    if (lessons !== null) return;
    let cancelled = false;
    async function fetchLessons() {
      setLessonsLoading(true);
      setLessonsError("");
      try {
        const res = await apiFetch("/api/v1/dashboard/lessons");
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || "Failed to fetch stories");
        }
        const data = await res.json();
        if (!cancelled) setLessons(data.stories);
      } catch (err) {
        if (!cancelled) setLessonsError(err.message);
      } finally {
        if (!cancelled) setLessonsLoading(false);
      }
    }
    fetchLessons();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessons === null]);

  const handleGenerateDownloadAudio = useCallback(async (audioKey) => {
    if (!activeStory || downloadAudioGenerating) return;
    setDownloadAudioGenerating(audioKey);
    try {
      const res = await apiFetch(`/api/v1/admin/generate-download-audio/${activeStory}/${audioKey}`, {
        method: "POST",
      });
      if (res.ok) {
        // Refresh status
        const statusRes = await apiFetch(`/api/v1/admin/download-audio-status/${activeStory}`);
        if (statusRes.ok) {
          const data = await statusRes.json();
          setDownloadAudioStatus(data.status);
        }
      } else {
        const data = await res.json().catch(() => null);
        setError(data?.detail || "Failed to generate audio");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadAudioGenerating(null);
    }
  }, [activeStory, downloadAudioGenerating]);

  const handleLearn = useCallback((storyId) => {
    trackEvent("learning_story_opened", { story_id: storyId });
    setLearningStoryId(storyId);
    setActiveTab("Learn");
  }, []);

  const handleBackFromLearn = useCallback(() => {
    setLearningStoryId(null);
    setLessons(null);
    setActiveTab("History");
  }, []);

  const handleNewStory = useCallback(() => {
    setUserPrompt("");
    setSuperTextPrompt("");
    setActiveStory(null);
    setStory(null);
    setError("");
  }, []);

  const handleGenerateStory = useCallback(async () => {
    if (!userPrompt.trim()) return;
    setError("");
    setLoadingAction("generate-story");

    try {
      const res = await apiFetch("/api/v1/admin/generate-story", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_prompt: userPrompt,
          active_story: activeStory,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to generate story");
      }

      const data = await res.json();
      trackEvent("story_generated", { story_id: data.story_id });
      setStory(data);
      setActiveStory(data.story_id);
      setUserPrompt("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [userPrompt, activeStory]);

  const handleGenerateWordsInfo = useCallback(async () => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("generate-words-info");

    try {
      const res = await apiFetch("/api/v1/admin/generate-word-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to generate words info");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);


  const handleDeleteWordsInfo = useCallback(async () => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("delete-words-info");

    try {
      const res = await apiFetch("/api/v1/admin/delete-word-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to delete words info");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);


  const handleGenerateStoryAudio = useCallback(async (voiceId) => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("generate-story-audio");

    try {
      const res = await apiFetch("/api/v1/admin/generate-story-audio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory, voice_id: voiceId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to generate story audio");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);

  const handleDeleteStoryAudio = useCallback(async () => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("delete-story-audio");

    try {
      const res = await apiFetch("/api/v1/admin/delete-story-audio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to delete story audio");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);


  const handleGenerateAvatar = useCallback(async (prompt) => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("generate-avatar");

    try {
      const res = await apiFetch("/api/v1/admin/generate-avatar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory, prompt }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to generate avatar");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);

  const handleDeleteAvatar = useCallback(async () => {
    if (!activeStory) return;
    setError("");
    setLoadingAction("delete-avatar");

    try {
      const res = await apiFetch("/api/v1/admin/delete-avatar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: activeStory }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to delete avatar");
      }

      const data = await res.json();
      setStory(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [activeStory]);

  const handleBuildStory = useCallback(async () => {
    if (!superTextPrompt.trim()) return;
    setError("");
    setLoadingAction("build-story");

    try {
      const res = await apiFetch("/api/v1/admin/build-story", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: superTextPrompt }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to build story");
      }

      const data = await res.json();
      setStory(data);
      setActiveStory(data.story_id);
      setSuperTextPrompt("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, [superTextPrompt]);

  const handleDeleteStory = useCallback(async (storyId) => {
    const res = await apiFetch("/api/v1/admin/delete-story", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active_story: storyId }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.detail || "Failed to delete story");
    }

    // If the deleted story is currently active, reset chat state
    if (activeStory === storyId) {
      setUserPrompt("");
      setActiveStory(null);
      setStory(null);
      setError("");
    }
  }, [activeStory]);

  const handleCreateNewStory = useCallback(() => {
    handleNewStory();
    setActiveTab("Chat");
  }, [handleNewStory]);

  const handleBackToManageLessons = useCallback(() => {
    setActiveTab("ManageLessons");
  }, []);

  const handleSeeStory = useCallback(async (storyId) => {
    setActiveTab("Chat");
    setUserPrompt("");
    setActiveStory(storyId);
    setError("");
    setLoadingAction("load-story");

    try {
      const res = await apiFetch("/api/v1/admin/generate-story", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active_story: storyId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to load story");
      }

      const data = await res.json();
      setStory(data);
      setActiveStory(data.story_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  }, []);

  return (
    <div className={s.page}>
      {activeTab === "History" && <header className={s.topBar}>
        <span className={s.topLogo}>
          Tala svenska
          <span className={s.topLogoDot} />
        </span>
        <img src="/swedish_flag.svg" alt="Swedish flag" className={s.topFlag} />
      </header>}
      <div className={s.mainArea}>
      <Sidebar activeTab={activeTab === "Chat" ? "ManageLessons" : activeTab} onTabChange={setActiveTab} onLogout={onLogout} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} userEmail={userEmail} userRole={userRole} supportUnreadCount={supportUnreadCount} />
      <div className={s.content}>
        {activeTab === "Learn" && learningStoryId ? (
          <main className={s.mainCol}>
            <LearningBoard storyId={learningStoryId} onBack={handleBackFromLearn} />
          </main>
        ) : activeTab === "AiTutor" ? (
          <main className={s.mainCol}>
            <AiTutorPanel userEmail={userEmail} />
          </main>
        ) : activeTab === "ManageLessons" ? (
          <main className={s.mainCol}>
            <ManageLessonsPanel onOpenInWorkshop={handleSeeStory} onDeleteStory={handleDeleteStory} onCreateNewStory={handleCreateNewStory} />
          </main>
        ) : activeTab === "Monitor" ? (
          <main className={s.mainCol}>
            <MonitorPanel />
          </main>
        ) : activeTab === "AiLogs" ? (
          <main className={s.mainCol}>
            <AiLogsPanel />
          </main>
        ) : activeTab === "Profile" ? (
          <main className={s.mainCol}>
            <ProfilePanel />
          </main>
        ) : activeTab === "Sentences" ? (
          <main className={s.mainCol}>
            <SentenceBoard />
          </main>
        ) : activeTab === "Support" ? (
          <main className={s.mainCol}>
            <SupportPanel onUnreadChange={fetchSupportUnreadCount} />
          </main>
        ) : activeTab === "History" ? (
          <main className={s.mainCol}>
            <HistoryPanel onLearn={handleLearn} userEmail={userEmail} stories={lessons} loading={lessonsLoading} error={lessonsError} />
          </main>
        ) : (
          <>
            <main className={s.storyCol}>
              <div className={s.workshopHeader}>
                <button className={s.backBtn} onClick={handleBackToManageLessons}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Manage Lessons
                </button>
                <h2 className={s.workshopTitle}>Story Workshop</h2>
              </div>
              <StoryPanel
                story={story}
                loadingAction={loadingAction}
                areWordsInfoGenerated={story?.are_words_info_generated ?? false}
                areQuizzesGenerated={story?.are_quizzes_generated ?? false}
                areAudioLessonGenerated={story?.are_audio_lesson_generated ?? false}
                areAudioQuizzesGenerated={story?.are_audio_quizzes_generated ?? false}
                hasAvatar={story?.has_avatar ?? false}
              />
            </main>
            <aside className={`${s.chatCol} ${chatOpen ? s.chatColWide : s.chatColClosed}`}>
              <button
                className={s.chatToggle}
                onClick={() => setChatOpen((v) => !v)}
                title={chatOpen ? "Hide workshop" : "Show workshop"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  {chatOpen
                    ? <path d="M9 18l6-6-6-6" />
                    : <path d="M15 18l-6-6 6-6" />}
                </svg>
              </button>
              {chatOpen && (
                <ChatPanel
                  chatMode={chatMode}
                  onChatModeChange={setChatMode}
                  userPrompt={userPrompt}
                  onUserPromptChange={setUserPrompt}
                  superTextPrompt={superTextPrompt}
                  onSuperTextChange={setSuperTextPrompt}
                  onGenerateStory={handleGenerateStory}
                  onBuildStory={handleBuildStory}
                  onGenerateWordsInfo={handleGenerateWordsInfo}
                  onDeleteWordsInfo={handleDeleteWordsInfo}
                  onGenerateStoryAudio={handleGenerateStoryAudio}
                  onDeleteStoryAudio={handleDeleteStoryAudio}
                  story={story}
                  onGenerateAvatar={handleGenerateAvatar}
                  onDeleteAvatar={handleDeleteAvatar}
                  onNewStory={handleNewStory}
                  loadingAction={loadingAction}
                  error={error}
                  activeStory={activeStory}
                  areWordsInfoGenerated={story?.are_words_info_generated ?? false}
                  areAudioLessonGenerated={story?.are_audio_lesson_generated ?? false}
                  hasAvatar={story?.has_avatar ?? false}
                  downloadAudioStatus={downloadAudioStatus}
                  downloadAudioGenerating={downloadAudioGenerating}
                  onGenerateDownloadAudio={handleGenerateDownloadAudio}
                />
              )}
            </aside>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
